import FingerprintJS from '@fingerprintjs/fingerprintjs';

let fpPromise = null;
let cachedResult = null;

/**
 * Initializes and loads the FingerprintJS agent once.
 * PCI-DSS Compliant: Runs fully in-memory, inspects only hardware/browser
 * attributes (screen, canvas, webgl, timezone), and never accesses form fields.
 */
function getAgent() {
    if (typeof window === 'undefined' || typeof window.document === 'undefined') {
        return Promise.resolve(null);
    }

    if (!fpPromise) {
        fpPromise = FingerprintJS.load({
            monitoring: false,
        }).catch((err) => {
            console.warn('[DeviceFingerprint] Failed to load fingerprint agent:', err);
            return null;
        });
    }
    return fpPromise;
}

/**
 * Retrieves the device fingerprint payload containing:
 * - visitor_id: Unique 32-char visitor hash
 * - telemetry: JSON string of non-sensitive browser & device signals
 *
 * @param {number} timeoutMs - Timeout fallback in milliseconds (default: 2500ms)
 * @returns {Promise<{visitor_id: string, telemetry: string}|null>}
 */
export async function getDeviceFingerprint(timeoutMs = 2500) {
    if (typeof window === 'undefined') {
        return null;
    }

    if (cachedResult) {
        return cachedResult;
    }

    try {
        const timeoutPromise = new Promise((resolve) =>
            setTimeout(() => resolve(null), timeoutMs)
        );

        const calculationPromise = (async () => {
            const fp = await getAgent();
            if (!fp) return null;

            const result = await fp.get();
            const { visitorId, components } = result;

            // Extract only safe, non-sensitive hardware & browser attributes
            const safeTelemetry = {};
            if (components) {
                const allowedKeys = [
                    'screenResolution',
                    'availableScreenResolution',
                    'colorDepth',
                    'timezone',
                    'platform',
                    'touchSupport',
                    'webglVendorAndRenderer',
                    'hardwareConcurrency',
                    'deviceMemory',
                    'languages',
                    'browserLanguage',
                    'colorGamut',
                    'contrast',
                    'forcedColors',
                    'monochrome',
                    'reducedMotion',
                    'hdr',
                ];

                for (const key of allowedKeys) {
                    if (components[key]?.value !== undefined) {
                        safeTelemetry[key] = components[key].value;
                    }
                }

                // Safely capture canvas & audio hashes if present
                if (components.canvas?.value) {
                    safeTelemetry.canvas = typeof components.canvas.value === 'object'
                        ? JSON.stringify(components.canvas.value)
                        : String(components.canvas.value);
                }
                if (components.audio?.value) {
                    safeTelemetry.audio = String(components.audio.value);
                }
            }

            return {
                visitor_id: visitorId,
                telemetry: JSON.stringify(safeTelemetry),
            };
        })();

        const outcome = await Promise.race([calculationPromise, timeoutPromise]);
        if (outcome) {
            cachedResult = outcome;
        }
        return outcome;
    } catch (err) {
        console.warn('[DeviceFingerprint] Error generating device fingerprint:', err);
        return null;
    }
}
